import { useContext } from "react";
import { Field } from "react-final-form";
import { useIntl } from "react-intl";
import { FieldArray } from "react-final-form-arrays";
import Section from "../../../common/section";
import { DropdownInput, TextInput } from "../../../common/fields";
import * as Validations from "../../../common/form/validator";
import { GlobalConstants } from "../../../../constants";
import { Button } from "primereact/button";
import { FormContext } from "../../../common/context";

const AudienceDetailsForm = ({
  departments = [],
  designations = [],
  managers = [],
  customFields = [],
  error = null,
  disabled = false,
}) => {
  const intl = useIntl();
  const formProvider = useContext(FormContext);

  const allFields = [
    {
      id: "Designation",
      name: "designation",
      value: "Designation",
      type: "Dropdown",
      i18n: true,
      options: designations?.map((item) => ({
        name: item.name || item.value,
        id: item.id,
        i18n: false,
        value: item.id,
      })),
    },
    {
      id: "Department",
      name: "department",
      value: "Department",
      type: "Dropdown",
      i18n: true,
      options: departments?.map((item) => ({
        name: item.name || item.value,
        id: item.id,
        i18n: false,
        value: item.id,
      })),
    },
    {
      id: "ManagerId",
      name: "managerId",
      value: "ManagerId",
      type: "Dropdown",
      i18n: true,
      options: managers?.map((item) => ({
        name: item.email,
        id: item.id,
        i18n: false,
        value: item.id,
      })),
    },
    ...customFields
      .filter((item) => item.type !== 5)
      .map((item) => ({
        ...item,
        type: GlobalConstants.FIELD_TYPES.find((type) => type.id === item.type)
          ?.name,
      })),
  ];

  const validateNameCallBack = (value) => {
    if (error?.response === "DUPLICATE_NAME" && value === error.values) {
      return "nameIsUse";
    }

    return undefined;
  };

  return (
    <div className="flex flex-column gap-4">
      <Section title="identity">
        <div className="grid">
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="name"
              disabled={disabled}
              component={TextInput}
              type="text"
              label={intl.formatMessage({
                id: "name",
              })}
              placeholder={intl.formatMessage({
                id: "name",
              })}
              required
              validate={Validations.composeValidators(
                Validations.required,
                Validations.validateWithCallback(validateNameCallBack)
              )}
            />
          </div>
        </div>
      </Section>
      <Section title="filters">
        <FieldArray name="data">
          {({ fields }) =>
            fields.map((name, index) => {
              const field = allFields.find(
                (item) => item.id === fields?.value?.[index]?.columeName
              );
              return (
                <div className="grid" key={index}>
                  {(index > 0 && (
                    <div className="col-12 md:col-2 lg:col-2">
                      <Field
                        name={`${name}.operator`}
                        component={DropdownInput}
                        disabled={disabled}
                        options={GlobalConstants.LEARNING_OPERATORS.map(
                          (item) => ({
                            name: item.name,
                            id: item.id,
                            i18n: true,
                            value: item.id,
                          })
                        )}
                        label={intl.formatMessage({
                          id: "must_have",
                        })}
                        placeholder={intl.formatMessage({
                          id: "must_have",
                        })}
                        required={field ? true : false}
                        validate={
                          field
                            ? Validations.composeValidators(
                                Validations.required
                              )
                            : null
                        }
                      />
                    </div>
                  )) || (
                    <div className="col-12 md:col-2 lg:col-2 flex align-items-center">
                      {intl.formatMessage({ id: "specify_rules" })}
                    </div>
                  )}
                  <div className="col-12 md:col-3 lg:col-3">
                    <Field
                      name={`${name}.columeName`}
                      component={DropdownInput}
                      disabled={disabled}
                      label={intl.formatMessage({
                        id: "field",
                      })}
                      filter
                      options={allFields}
                      placeholder={intl.formatMessage({
                        id: "field",
                      })}
                    />
                  </div>
                  <div className="col-12 md:col-3 lg:col-3">
                    <Field
                      name={`${name}.action`}
                      component={DropdownInput}
                      disabled={disabled}
                      options={GlobalConstants.EQUALITY_OPERATORS.map(
                        (item) => ({
                          name: item.name,
                          id: item.id,
                          i18n: true,
                          value: item.id,
                        })
                      )}
                      label={intl.formatMessage({
                        id: "operator",
                      })}
                      placeholder={intl.formatMessage({
                        id: "operator",
                      })}
                      required={field ? true : false}
                      validate={
                        field
                          ? Validations.composeValidators(Validations.required)
                          : null
                      }
                    />
                  </div>
                  {(field && fields?.value?.[index]?.action !== 2 && (
                    <>
                      <div className="col-12 md:col-3 lg:col-3">
                        {((field.type.toLowerCase() === "text" ||
                          field.type.toLowerCase() === "number") && (
                          <Field
                            name={`${name}.value`}
                            component={TextInput}
                            type={field.type}
                            disabled={disabled}
                            placeholder={
                              field.i18n
                                ? intl.formatMessage({
                                    id: field.label || field.name,
                                  })
                                : field.label || field.name
                            }
                            label={
                              field.i18n
                                ? intl.formatMessage({
                                    id: field.label || field.name,
                                  })
                                : field.label || field.name
                            }
                            required={field ? true : false}
                            validate={
                              field
                                ? Validations.composeValidators(
                                    Validations.required
                                  )
                                : null
                            }
                          />
                        )) ||
                          null}
                        {((field.type.toLowerCase() === "dropdown" ||
                          field.type.toLowerCase() === "boolean") && (
                          <Field
                            name={`${name}.value`}
                            component={DropdownInput}
                            readOnly={field.readOnly}
                            disabled={disabled}
                            editable={field.editable || false}
                            options={field.options || []}
                            placeholder={
                              field.i18n
                                ? intl.formatMessage({
                                    id: field.label || field.name,
                                  })
                                : field.label || field.name
                            }
                            label={
                              field.i18n
                                ? intl.formatMessage({
                                    id: field.label || field.name,
                                  })
                                : field.label || field.name
                            }
                            required={field ? true : false}
                            validate={
                              field
                                ? Validations.composeValidators(
                                    Validations.required
                                  )
                                : null
                            }
                          />
                        )) ||
                          null}
                      </div>
                    </>
                  )) ||
                    null}
                  {(field && fields?.value?.[index]?.action === 2 && (
                    <div className="col-12 md:col-3 lg:col-3">
                      <Field
                        name={`${name}.value`}
                        component={TextInput}
                        readOnly={field.readOnly}
                        disabled={disabled}
                        editable={field.editable || false}
                        required={field ? true : false}
                        label={
                          field.i18n
                            ? intl.formatMessage({
                                id: field.label || field.name,
                              })
                            : field.label || field.name
                        }
                        placeholder={
                          field.i18n
                            ? intl.formatMessage({
                                id: field.label || field.name,
                              })
                            : field.label || field.name
                        }
                        validate={
                          field
                            ? Validations.composeValidators(
                                Validations.required
                              )
                            : null
                        }
                      />
                    </div>
                  )) ||
                    null}
                  {(field && (
                    <div className="col-12 md:col-1 lg:col-1">
                      <div className="flex gap-1">
                        {(index === fields.length - 1 && !disabled && (
                          <>
                            <Button
                              type="button"
                              icon="pi pi-plus"
                              style={{ marginTop: "21px" }}
                              onClick={() =>
                                formProvider.push("data", {
                                  operator: GlobalConstants.LEARNING_OPERATORS.find(
                                    (op) => op.default
                                  )?.id,
                                })
                              }
                            />
                          </>
                        )) ||
                          null}
                        {(fields.length > 1 && !disabled && (
                          <Button
                            type="button"
                            icon="pi pi-trash"
                            style={{ marginTop: "21px" }}
                            onClick={() => fields.remove(index)}
                          />
                        )) ||
                          null}
                      </div>
                    </div>
                  )) ||
                    null}
                </div>
              );
            })
          }
        </FieldArray>
      </Section>
    </div>
  );
};

export default AudienceDetailsForm;
